var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Reject + Request Changes buttons with their modals.
 * Reusable between the proposal detail page and the edit-in-review footer.
 *
 * TASK-080 extends the reject flow with:
 *   - A "Uložit a zamítnout" primary action that chains save + reject when the
 *     reviewer has edits in the form (rendered only when the parent passes
 *     {@link Props.onSaveAndReject}).
 *   - A three-way confirmation dialog shown when the reviewer clicks "Zamítnout"
 *     while the form is dirty: Save+Reject / Discard+Reject / Cancel. The
 *     downstream reason+comment modal is opened after the choice.
 */
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { Button, Layout, MessageBanner, Modal, Select, TextArea, } from '@folio/stripes/components';
import { RejectionReason } from '../../types/api';
import { rejectProposal, requestChanges } from '../../api/proposalService';
import { getErrorInfo } from '../../utils/getErrorCode';
import { getProposalErrorMessageId } from '../../utils/getErrorMessageId';
import { getErrorKind } from '../../utils/errorKind';
import { isSaveOutcomeUnknownError } from '../../utils/saveOutcomeUnknown';
import { PROPOSALS_QUERY_ROOT } from '../../hooks/useProposalList';
import ActionHelp from '../ActionHelp';
/** A button and, when the caller gave the values, its help — kept together on wrap. */
var WithHelp = function (_a) {
    var testKey = _a.testKey, labelId = _a.labelId, helpId = _a.helpId, helpValues = _a.helpValues, children = _a.children;
    return (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center' } },
        children,
        helpValues && (React.createElement(ActionHelp, { testKey: testKey, labelId: labelId, helpId: helpId, helpValues: helpValues }))));
};
var modalFooterStyle = {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    marginTop: '16px',
};
var RejectRequestChangesButtons = function (_a) {
    var id = _a.id, successReturnUrl = _a.successReturnUrl, onComplete = _a.onComplete, _b = _a.isDirty, isDirty = _b === void 0 ? false : _b, _c = _a.busy, busy = _c === void 0 ? false : _c, onSaveAndReject = _a.onSaveAndReject, helpValues = _a.helpValues;
    var intl = useIntl();
    var history = useHistory();
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _d = useState(false), showUnsavedRejectModal = _d[0], setShowUnsavedRejectModal = _d[1];
    var _e = useState(false), showRejectModal = _e[0], setShowRejectModal = _e[1];
    var _f = useState(false), showRequestChangesModal = _f[0], setShowRequestChangesModal = _f[1];
    // Tracks which branch the reviewer selected from the unsaved-changes dialog
    // (or from the dedicated "Uložit a zamítnout" button). Drives whether the
    // reason-modal submit calls rejectProposal directly or onSaveAndReject.
    var _g = useState('idle'), rejectMode = _g[0], setRejectMode = _g[1];
    var _h = useState(''), rejectReason = _h[0], setRejectReason = _h[1];
    var _j = useState(''), rejectComment = _j[0], setRejectComment = _j[1];
    var _k = useState(''), requestChangesComment = _k[0], setRequestChangesComment = _k[1];
    var _l = useState(null), actionError = _l[0], setActionError = _l[1];
    var _m = useState(false), saveAndRejectInFlight = _m[0], setSaveAndRejectInFlight = _m[1];
    var closeRejectModal = function () {
        setShowRejectModal(false);
        setRejectReason('');
        setRejectComment('');
        setRejectMode('idle');
    };
    var closeRequestChangesModal = function () { setShowRequestChangesModal(false); setRequestChangesComment(''); };
    var closeUnsavedRejectModal = function () { return setShowUnsavedRejectModal(false); };
    var handleSuccess = function (closeModal) {
        closeModal();
        setActionError(null);
        queryClient.invalidateQueries(['proposal', id]);
        queryClient.invalidateQueries([PROPOSALS_QUERY_ROOT]);
        onComplete === null || onComplete === void 0 ? void 0 : onComplete();
        if (successReturnUrl)
            history.push(successReturnUrl);
    };
    // CZNA-608 — same three-way split as useProposalReviewActions.handleError. This copy
    // branched on 409 only, so one backend refusal produced two different explanations
    // depending on which screen the user rejected from: the honest permission sentence on
    // the proposal detail, "rejection failed" in the edit-in-review footer.
    var handleError = function (error, fallbackKey) { return __awaiter(void 0, void 0, void 0, function () {
        var errorInfo, messageId;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, getErrorInfo(error)];
                case 1:
                    errorInfo = _a.sent();
                    messageId = fallbackKey;
                    if (errorInfo.status === 409) {
                        messageId = getProposalErrorMessageId(errorInfo);
                    }
                    else if (errorInfo.status === 403) {
                        messageId = 'ui-czna-authorities.proposals.detail.forbidden';
                    }
                    else if (getErrorKind(errorInfo) === 'timeout') {
                        // CZNA-1625 — no answer in time: the action may have been carried out. "Se nezdařilo"
                        // would invite repeating it; the timeout sentence asks to check the state first.
                        messageId = getProposalErrorMessageId(errorInfo);
                    }
                    setActionError(intl.formatMessage({ id: messageId }));
                    return [2 /*return*/];
            }
        });
    }); };
    var rejectMutation = useMutation(function () {
        if (!rejectReason)
            throw new Error('Rejection reason is required');
        return rejectProposal(ky, id, {
            rejectionReason: rejectReason,
            reviewComment: rejectComment.trim(),
        });
    }, {
        onSuccess: function () { return handleSuccess(closeRejectModal); },
        onError: function (error) { return handleError(error, 'ui-czna-authorities.proposals.detail.rejectError'); },
    });
    var requestChangesMutation = useMutation(function () { return requestChanges(ky, id, { reviewComment: requestChangesComment }); }, {
        onSuccess: function () { return handleSuccess(closeRequestChangesModal); },
        onError: function (error) { return handleError(error, 'ui-czna-authorities.proposals.detail.requestChangesError'); },
    });
    var rejectionReasonOptions = useMemo(function () { return __spreadArray([
        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.rejectModal.reason.placeholder' }) }
    ], Object.values(RejectionReason).map(function (reason) { return ({
        value: reason,
        label: intl.formatMessage({ id: "ui-czna-authorities.rejectionReason.".concat(reason) }),
    }); }), true); }, [intl]);
    /**
     * Entry point for the reject button: when dirty and the parent supports
     * Save+Reject, open the three-way unsaved-changes dialog; otherwise fall
     * back to the legacy direct-to-reason-modal flow ("Discard" semantics, since
     * any edits stay in form state and are simply not persisted).
     */
    var handleRejectClick = function () {
        if (isDirty && onSaveAndReject) {
            setShowUnsavedRejectModal(true);
        }
        else {
            // No edits or no save chain available — open the reason modal directly,
            // identical to the legacy behaviour.
            setRejectMode('discardAndReject');
            setShowRejectModal(true);
        }
    };
    /** Entry for the dedicated "Uložit a zamítnout" primary button. */
    var handleSaveAndRejectClick = function () {
        setRejectMode('saveAndReject');
        setShowRejectModal(true);
    };
    /** Three-way dialog: chose Save+Reject → open reason modal, then call save+reject chain. */
    var handleUnsavedSaveAndReject = function () {
        setShowUnsavedRejectModal(false);
        setRejectMode('saveAndReject');
        setShowRejectModal(true);
    };
    /** Three-way dialog: chose Discard+Reject → open reason modal, run plain reject. */
    var handleUnsavedDiscardAndReject = function () {
        setShowUnsavedRejectModal(false);
        setRejectMode('discardAndReject');
        setShowRejectModal(true);
    };
    /**
     * Invoked from the reason/comment modal's Confirm button. Branches on the
     * reject mode that was set when the user picked a path from the three-way
     * dialog (or clicked the dedicated Save+Reject button).
     */
    var handleRejectConfirm = function () { return __awaiter(void 0, void 0, void 0, function () {
        var error_1, message;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    // CZNA-1531 (F7) — the comment is required; the backend refuses a reject without one.
                    if (!rejectReason || !rejectComment.trim())
                        return [2 /*return*/];
                    if (!(rejectMode === 'saveAndReject' && onSaveAndReject)) return [3 /*break*/, 6];
                    setActionError(null);
                    setSaveAndRejectInFlight(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, onSaveAndReject({
                            rejectionReason: rejectReason,
                            reviewComment: rejectComment.trim(),
                        })];
                case 2:
                    _a.sent();
                    // Parent handler is responsible for query invalidation and navigation;
                    // close the modal here so the UI returns to a clean state.
                    closeRejectModal();
                    onComplete === null || onComplete === void 0 ? void 0 : onComplete();
                    return [3 /*break*/, 5];
                case 3:
                    error_1 = _a.sent();
                    // CZNA-1625 — the save leg went unanswered: the form's own banner now says so and
                    // offers "Načíst znovu". The reject never ran, and the modal over the form would hide
                    // that banner — close it rather than repeat the sentence in a second place.
                    if (isSaveOutcomeUnknownError(error_1)) {
                        closeRejectModal();
                        return [2 /*return*/];
                    }
                    message = error_1 instanceof Error ? error_1.message : intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.rejectError' });
                    setActionError(message);
                    return [3 /*break*/, 5];
                case 4:
                    setSaveAndRejectInFlight(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
                case 6:
                    // Default branch (no edits OR Discard+Reject): legacy single-call reject.
                    rejectMutation.mutate();
                    return [2 /*return*/];
            }
        });
    }); };
    var isRejectBusy = rejectMutation.isLoading || saveAndRejectInFlight;
    return (React.createElement(React.Fragment, null,
        actionError && React.createElement(MessageBanner, { type: "error" }, actionError),
        onSaveAndReject && (React.createElement(WithHelp, { testKey: "saveAndReject", labelId: "ui-czna-authorities.proposalForm.saveAndReject", helpId: "ui-czna-authorities.proposalForm.saveAndReject.effect", helpValues: helpValues },
            React.createElement(Button, { buttonStyle: "primary", onClick: handleSaveAndRejectClick, disabled: isRejectBusy || busy },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.saveAndReject" })))),
        React.createElement(WithHelp, { testKey: "reject", labelId: "ui-czna-authorities.proposals.detail.reject", helpId: "ui-czna-authorities.proposals.detail.reject.effect", helpValues: helpValues },
            React.createElement(Button, { buttonStyle: "danger", onClick: handleRejectClick, disabled: isRejectBusy || busy },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.detail.reject" }))),
        React.createElement(WithHelp, { testKey: "requestChanges", labelId: "ui-czna-authorities.proposals.detail.requestChanges", helpId: "ui-czna-authorities.proposals.detail.requestChanges.effect", helpValues: helpValues },
            React.createElement(Button, { onClick: function () { return setShowRequestChangesModal(true); }, disabled: requestChangesMutation.isLoading || busy },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.detail.requestChanges" }))),
        React.createElement(Modal, { open: showUnsavedRejectModal, label: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.unsavedRejectModal.heading' }), dismissible: true, onClose: closeUnsavedRejectModal },
            React.createElement(Layout, { className: "padding-all-gutter" },
                React.createElement("p", null, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.unsavedRejectModal.message' })),
                React.createElement("div", { style: modalFooterStyle },
                    React.createElement(Button, { onClick: closeUnsavedRejectModal },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.unsavedRejectModal.cancel" })),
                    React.createElement(Button, { buttonStyle: "danger", onClick: handleUnsavedDiscardAndReject },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.unsavedRejectModal.discardAndReject" })),
                    React.createElement(Button, { buttonStyle: "primary", onClick: handleUnsavedSaveAndReject },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.unsavedRejectModal.saveAndReject" }))))),
        React.createElement(Modal, { open: showRejectModal, label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.rejectModal.heading' }), dismissible: true, onClose: closeRejectModal },
            React.createElement(Layout, { className: "padding-all-gutter" },
                React.createElement(Select, { label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.rejectModal.reason' }), dataOptions: rejectionReasonOptions, value: rejectReason, onChange: function (e) { return setRejectReason(e.target.value); }, required: true }),
                React.createElement(TextArea, { label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.rejectModal.comment' }), value: rejectComment, onChange: function (e) { return setRejectComment(e.target.value); }, required: true }),
                React.createElement("div", { style: modalFooterStyle },
                    React.createElement(Button, { onClick: closeRejectModal },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.detail.rejectModal.cancel" })),
                    React.createElement(Button, { buttonStyle: "danger", onClick: handleRejectConfirm, disabled: !rejectReason || !rejectComment.trim() || isRejectBusy },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.detail.rejectModal.confirm" }))))),
        React.createElement(Modal, { open: showRequestChangesModal, label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.requestChangesModal.heading' }), dismissible: true, onClose: closeRequestChangesModal },
            React.createElement(Layout, { className: "padding-all-gutter" },
                React.createElement(TextArea, { label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.requestChangesModal.comment' }), placeholder: intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.requestChangesModal.commentPlaceholder' }), value: requestChangesComment, onChange: function (e) { return setRequestChangesComment(e.target.value); }, required: true }),
                React.createElement("div", { style: modalFooterStyle },
                    React.createElement(Button, { onClick: closeRequestChangesModal },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.detail.requestChangesModal.cancel" })),
                    React.createElement(Button, { buttonStyle: "primary", onClick: function () { return requestChangesMutation.mutate(); }, disabled: !requestChangesComment.trim() || requestChangesMutation.isLoading },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.detail.requestChangesModal.confirm" })))))));
};
export default RejectRequestChangesButtons;
